import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Landing from "./pages/Landing";
import Gallery from "./pages/Gallery";
import Pricing from "./pages/Pricing";
import Upsell from "./pages/Upsell";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Assets from "./pages/Assets";
import Creations from "./pages/Creations";
import Keys from './pages/Keys';
import Legal from './pages/Legal';
import NotFound from './pages/NotFound';
import Credits from './pages/Credits';
import Reset from './pages/Reset';
import Influencers from "./pages/Influencers";
import Studio from "./pages/Studio";
import Admin from "./pages/Admin";
import AppShell from "./components/AppShell";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { captureRef } from "./lib/affiliate";

captureRef();

// React Router changes the URL for a link like /#whitelabel but never scrolls:
// hash handling is not its job. The header is also fixed at 64px, so even the
// browser's own anchor jump would leave the heading hidden underneath it.
function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return undefined;
    }
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    let timer;
    const go = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 76;
        window.scrollTo({ top, behavior: "smooth" });
      } else if (tries++ < 20) {
        // Arriving from another route, the target section may not be mounted yet.
        timer = setTimeout(go, 50);
      }
    };
    go();
    return () => clearTimeout(timer);
  }, [pathname, hash]);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0E0E10] text-white/50">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0E0E10] text-white/50">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/upgrade/:tier" element={<Upsell />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/register" element={<Auth mode="register" />} />
            <Route path="/reset" element={<Reset />} />
            {/* A payment processor checks these exist before it approves a
                seller, and a buyer checks them before parting with money. */}
            <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/legal/:doc" element={<Legal />} />
            {/* The panel pages share one shell: persistent sidebar, catalogue
                modals and the video flag live in AppShell, the page renders
                through its <Outlet/>. The studio stays out on purpose — it is
                a full-screen canvas with its own header. */}
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/app" element={<Dashboard />} />
              <Route path="/app/assets" element={<Assets />} />
              <Route path="/app/keys" element={<Keys />} />
              <Route path="/app/credits" element={<Credits />} />
              {/* What the studio made, as opposed to what was uploaded to it. */}
              <Route path="/app/creations" element={<Creations />} />
              <Route path="/app/influencers" element={<Influencers />} />
            </Route>
            <Route path="/app/studio/:workflowId" element={<Protected><Studio /></Protected>} />
            <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
            {/* Sin esto, una URL mal escrita daba una pagina en blanco, que para
                quien la ve no se distingue de una app rota. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
